import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BackLink } from "../ui/BackLink";
import { useFollowUps, useNamedPeople, type FollowUp } from "../data";
import { cancelTask, completeTask, createTask, snoozeTask } from "../actions";
import { PersonPicker } from "../ui/PersonPicker";
import { count, formatDate } from "../hebrew";
import { IcCheck, IcClock, IcPlus, IcSearch } from "../ui/icons";

/**
 * Follow-ups — every open reminder, and a way to write one down.
 *
 * ## Why this screen exists
 *
 * Closing a reminder became possible when the three callables were finally wired
 * up; *finding* one never was. The Inbox loads forty items and a reminder that
 * falls off the end of that list is invisible — fine for a queue you work
 * through, useless when you know a reminder exists and need to get to it.
 *
 * And nothing could create one. A follow-up could only be born as a by-product:
 * logging a conversation with a promise attached, or an enquiry arriving from the
 * website. "Ring the Cohens next week" — the most ordinary thing anybody wants
 * from a CRM — had nowhere to go, which is part of why the list looked thinner
 * than the actual work.
 *
 * ## Overdue first, and said plainly
 *
 * Sorted by date with the overdue ones at the top, because that is the order a
 * person would read them in. A reminder pushed back repeatedly says so: something
 * postponed five times is a decision already made, and a screen that keeps
 * offering it without comment is not listening.
 */

/** The postponement options, as a person would say them. */
const LATER = [
  { label: "מחר", days: 1 },
  { label: "עוד 3 ימים", days: 3 },
  { label: "עוד שבוע", days: 7 },
  { label: "עוד חודש", days: 30 },
] as const;

function atNoon(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + days);
  // Midday, not midnight: a reminder due "tomorrow" should not be overdue the
  // moment tomorrow begins.
  d.setHours(12, 0, 0, 0);
  return d;
}

function dateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function TasksPage() {
  const follow = useFollowUps();
  /** `?new=1` opens the form on arrival, so the quick-add sheet can land here
   *  with the reminder ready to type rather than one tap short of it. */
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(params.get("new") === "1");

  /** Closes the form and drops the flag off the address, so a refresh does not
   *  reopen a form that was just finished with. */
  function closeForm() {
    setCreating(false);
    if (!params.has("new")) return;
    const next = new URLSearchParams(params);
    next.delete("new");
    setParams(next, { replace: true });
  }
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [term, setTerm] = useState("");

  const personIds = useMemo(
    () => [...new Set(follow.items.map((t) => t.personId).filter((id): id is string => Boolean(id)))],
    [follow.items],
  );
  // Every name, not the first thirty: 79 thank-you tasks arrived at once, and a
  // row without its name was a list of "להודות: פדיון כפרות" and nothing else.
  const people = useNamedPeople(personIds);
  const nameById = useMemo(
    () => new Map(people.data.map((p) => [p.id, p.displayName ?? ""])),
    [people.data],
  );

  // Rows closed on the tap, before the callable (up to ~2.4s cold) replies.
  // A failed write takes the id out again and the row returns beside the error.
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  const shown = useMemo(() => {
    const q = term.trim();
    const open = follow.items.filter((t) => !hidden.has(t.id));
    if (q === "") return open;
    return open.filter((t) =>
      [t.title, t.description, t.personId ? (nameById.get(t.personId) ?? "") : ""]
        .join(" ")
        .includes(q),
    );
  }, [follow.items, term, nameById, hidden]);

  const overdue = shown.filter((t) => t.overdue);
  const later = shown.filter((t) => !t.overdue);

  /** `leaves`: the action closes the task, so its row goes now. A snooze does
   *  not — the task stays open under its new date. */
  async function act(id: string, fn: () => Promise<unknown>, said: string, leaves = false) {
    setBusy(id);
    setErr(null);
    if (leaves) setHidden((s) => new Set(s).add(id));
    try {
      await fn();
      setNote(said);
    } catch (e) {
      if (leaves)
        setHidden((s) => {
          const n = new Set(s);
          n.delete(id);
          return n;
        });
      setErr(e instanceof Error ? e.message : "לא הצלחנו לבצע את הפעולה. נסו שוב.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>משימות</h1>
            <p className="sub">
              {follow.loading
                ? "טוענים…"
                : count(follow.items.length, { one: "משימה", many: "משימות", feminine: true }, "אין משימות פתוחות")}
              {overdue.length > 0 && ` · ${overdue.length} באיחור`}
            </p>
          </div>
        </div>

        <div className="searchbar">
          <IcSearch size={18} />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="חיפוש במשימות…"
            autoComplete="off"
          />
        </div>
      </header>

      <div className="body body-clear-nav">
        <BackLink fallback="/more" label="חזרה" />

        {note && <div className="notice info">{note}</div>}
        {err && <div className="notice error">{err}</div>}
        {follow.error && <div className="notice error">{follow.error}</div>}

        {creating ? (
          <NewFollowUp
            onClose={closeForm}
            onCreated={(said) => {
              closeForm();
              setNote(said);
            }}
          />
        ) : (
          <button className="btn btn-wide" onClick={() => setCreating(true)}>
            <IcPlus size={16} /> משימה חדשה
          </button>
        )}

        {follow.loading ? (
          <div className="skeleton" style={{ height: 140, marginTop: 12 }} />
        ) : shown.length === 0 ? (
          <div className="empty">
            <div
              className="empty-ico tint-green"
            >
              <IcCheck size={26} />
            </div>
            <h3>{term === "" ? "אין משימות פתוחות. כל הכבוד!" : "לא מצאנו כלום"}</h3>
            <p>
              {term === ""
                ? "אפשר להוסיף משימה כאן, או מתיעוד שיחה שנשאר בה משהו לעשות."
                : "נסו מילה אחרת."}
            </p>
          </div>
        ) : (
          <>
            {overdue.length > 0 && (
              <Section title="באיחור">
                {overdue.map((t) => (
                  <Row
                    key={t.id}
                    task={t}
                    name={t.personId ? (nameById.get(t.personId) ?? null) : null}
                    busy={busy === t.id}
                    act={act}
                  />
                ))}
              </Section>
            )}
            {later.length > 0 && (
              <Section title={overdue.length > 0 ? "בהמשך" : "פתוחות"}>
                {later.map((t) => (
                  <Row
                    key={t.id}
                    task={t}
                    name={t.personId ? (nameById.get(t.personId) ?? null) : null}
                    busy={busy === t.id}
                    act={act}
                  />
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-title">{title}</div>
      {children}
    </div>
  );
}

function Row({
  task,
  name,
  busy,
  act,
}: {
  task: FollowUp;
  name: string | null;
  busy: boolean;
  act: (id: string, fn: () => Promise<unknown>, said: string, leaves?: boolean) => Promise<void>;
}) {
  const [panel, setPanel] = useState<"none" | "later">("none");
  const [customDate, setCustomDate] = useState("");

  return (
    <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--line)" }}>
      <p style={{ margin: 0, fontWeight: 500 }}>{task.title}</p>

      {task.description && (
        <p className="muted" style={{ marginTop: 2, whiteSpace: "pre-wrap" }}>
          {task.description}
        </p>
      )}

      <p className="faint" style={{ marginTop: 2 }}>
        {task.dueAt ? formatDate(task.dueAt) : "בלי תאריך"}
        {name && task.personId && (
          <>
            {" · "}
            <Link to={`/people/${task.personId}`}>{name}</Link>
          </>
        )}
        {/* Said out loud rather than hidden: something pushed back this often is
            a decision somebody has already made without saying so. */}
        {task.snoozeCount >= 3 && ` · נדחתה ${task.snoozeCount} פעמים`}
      </p>

      {panel === "later" ? (
        <div style={{ marginTop: 8 }}>
          <div className="chips">
            {LATER.map((o) => (
              <button
                key={o.days}
                className="tagchip"
                disabled={busy}
                onClick={() =>
                  act(task.id, () => snoozeTask(task.id, atNoon(o.days).toISOString()), "דחינו.").then(
                    () => setPanel("none"),
                  )
                }
              >
                {o.label}
              </button>
            ))}
          </div>
          {/* The date picker the fixed week was standing in for. */}
          <div className="btn-row" style={{ marginTop: 8 }}>
            <input
              className="form-input"
              type="date"
              value={customDate}
              min={dateInput(atNoon(1))}
              onChange={(e) => setCustomDate(e.target.value)}
              disabled={busy}
              style={{ flex: 1 }}
            />
            <button
              className="btn"
              disabled={busy || customDate === ""}
              onClick={() =>
                act(
                  task.id,
                  () => snoozeTask(task.id, new Date(`${customDate}T12:00:00`).toISOString()),
                  "דחינו.",
                ).then(() => setPanel("none"))
              }
            >
              דחו לתאריך
            </button>
            <button className="btn" onClick={() => setPanel("none")} disabled={busy}>
              ביטול
            </button>
          </div>
        </div>
      ) : (
        <div className="btn-row" style={{ marginTop: 8 }}>
          <button
            className="btn"
            disabled={busy}
            onClick={() => act(task.id, () => completeTask(task.id), "סימנו כבוצע.", true)}
          >
            <IcCheck size={15} /> בוצע
          </button>
          <button className="btn" disabled={busy} onClick={() => setPanel("later")}>
            <IcClock size={15} /> אחר כך
          </button>
          {/*
            Cancelling is not completing, and the wording says so. A reminder
            created by mistake recorded as "done" would sit beside the real work
            and inflate every count built on it.
          */}
          <button
            className="btn"
            disabled={busy}
            onClick={() => act(task.id, () => cancelTask(task.id), "ביטלנו את המשימה.", true)}
          >
            לא רלוונטי
          </button>
        </div>
      )}
    </div>
  );
}

/** Writing one down. */
function NewFollowUp({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (said: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [due, setDue] = useState(dateInput(atNoon(1)));
  const [personId, setPersonId] = useState<string | null>(null);
  const [personName, setPersonName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (title.trim().length < 2) {
      setErr("כתבו מה צריך לעשות.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await createTask({
        title: title.trim(),
        dueAt: new Date(`${due}T12:00:00`).toISOString(),
        ...(personId ? { personId } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      onCreated("הוספנו את המשימה.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-title">משימה חדשה</div>
      <div className="card-pad">
        <div className="form-field">
          <span className="form-label">מה לעשות *</span>
          <input
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="להתקשר למשפחת כהן בעניין הבר מצווה"
            autoFocus
            disabled={busy}
          />
        </div>

        <div className="form-field">
          <span className="form-label">מתי *</span>
          <input
            className="form-input"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            disabled={busy}
          />
        </div>

        {/* Optional on purpose: "to order more candles" belongs to nobody. */}
        <div className="form-field">
          <span className="form-label">קשור למישהו</span>
          <PersonPicker
            personId={personId}
            personName={personName}
            disabled={busy}
            onPick={(person) => {
              setPersonId(person?.id ?? null);
              setPersonName(person?.name ?? "");
            }}
          />
        </div>

        <div className="form-field">
          <span className="form-label">פרטים</span>
          <textarea
            className="form-input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
          />
        </div>

        {err && <div className="form-error">{err}</div>}

        <div className="btn-row">
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? "שומרים…" : "שמרו"}
          </button>
          <button className="btn" onClick={onClose} disabled={busy}>
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}
